import { useState, useEffect, useCallback } from 'react';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // 检测 iOS 设备
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    // 检测 Android 设备
    const isAndroidDevice = /Android/.test(navigator.userAgent);
    setIsAndroid(isAndroidDevice);

    // 检查是否已在 PWA 内打开（独立模式 / 全屏 / minimal-ui，或 iOS 主屏）
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      (window.navigator as any).standalone === true;
    
    if (isStandalone) {
      setIsInstalled(true);
      setCanInstall(false);
      return;
    }

    // 监听 beforeinstallprompt 事件
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // iOS 和 Android 设备设置兜底逻辑
    if (isIOSDevice || isAndroidDevice) {
      setCanInstall(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = useCallback(async (iosInstructions?: string) => {
    // iOS 设备显示安装说明
    if (isIOS) {
      if (iosInstructions) {
        alert(iosInstructions);
      } else {
        alert('In Safari, tap the share button at the bottom, then select "Add to Home Screen"');
      }
      return;
    }

    // Android 设备使用原生安装
    if (isAndroid && deferredPrompt) {
      try {
        await (deferredPrompt as any).prompt();
        const { outcome } = await (deferredPrompt as any).userChoice;
        if (outcome === 'accepted') {
          setDeferredPrompt(null);
        }
      } catch (error) {
        console.error('PWA安装失败:', error);
      }
      return;
    }

    // 其他设备
    if (deferredPrompt) {
      try {
        await (deferredPrompt as any).prompt();
        const { outcome } = await (deferredPrompt as any).userChoice;
        setDeferredPrompt(null);
      } catch (error) {
        console.error('PWA安装失败:', error);
      }
    }
  }, [isIOS, isAndroid, deferredPrompt]);

  return {
    install,
    canInstall: canInstall && !isInstalled,
    isInstalled,
    isIOS,
    isAndroid,
  };
};

